// src/components/VerifyEmailBanner/VerifyEmailBanner.tsx
//
// Soft-gate banner shown inside the app for unverified password accounts.
// Dismissible for the current session; reappears next load until verified.
// - "Resend" re-sends the verification email (with cooldown)
// - "Open <provider>" deep-links straight to the inbox, filtered to Nisk mail
// - Polls in the background (via the hook) and auto-hides once verified

import React, { useEffect, useState } from 'react';
import type { UserInfo } from 'firebase/auth';
import { useEmailVerification } from '../../auth/useEmailVerification';
import styles from './VerifyEmailBanner.module.scss';

// Per-session dismissal. We deliberately use in-memory state (not storage)
// so the reminder returns on the next visit until the address is verified.
let dismissedThisSession = false;

const VerifyEmailBanner: React.FC = () => {
	const {
		needsVerification,
		verified,
		sending,
		checking,
		sendResult,
		cooldown,
		sendVerification,
		checkNow,
		inboxUrl,
		inboxLabel,
		user,
	} = useEmailVerification();

	const [dismissed, setDismissed] = useState(dismissedThisSession);
	const [justVerified, setJustVerified] = useState(false);

	// Flash a brief success state the moment verification is detected.
	useEffect(() => {
		if (!verified || needsVerification !== false || !user) return;

		// Only celebrate if we were previously waiting (avoid Google users).
		const isPassword = user.providerData.some(
			(p: UserInfo) => p.providerId === 'password',
		);
		if (!isPassword) return;

		setJustVerified(true);
		const t = setTimeout(() => setJustVerified(false), 4000);
		return () => clearTimeout(t);
	}, [verified, needsVerification, user]);

	if (justVerified) {
		return (
			<div
				className={`${styles.banner} ${styles.success}`}
				role='status'>
				<span
					className={styles.icon}
					aria-hidden='true'>
					✓
				</span>
				<p className={styles.text}>Your email is verified. Thanks!</p>
			</div>
		);
	}

	if (!needsVerification || dismissed) return null;

	const handleDismiss = () => {
		dismissedThisSession = true;
		setDismissed(true);
	};

	const handleOpenInbox = () => {
		if (inboxUrl) window.open(inboxUrl, '_blank', 'noopener,noreferrer');
	};

	return (
		<div
			className={styles.banner}
			role='status'>
			<span
				className={styles.icon}
				aria-hidden='true'>
				✉
			</span>

			<div className={styles.body}>
				<p className={styles.text}>
					<strong>Verify your email</strong> to secure your account.
					We sent a link to <strong>{user?.email}</strong>.
				</p>

				{sendResult && sendResult !== 'sent' && (
					<p className={styles.error}>{sendResult}</p>
				)}
				{sendResult === 'sent' && (
					<p className={styles.hint}>
						Verification email sent — check your inbox.
					</p>
				)}
				{checking && (
					<p className={styles.hint}>Checking verification status…</p>
				)}
			</div>

			<div className={styles.actions}>
				{inboxUrl && (
					<button
						type='button'
						className={styles.primaryBtn}
						onClick={handleOpenInbox}>
						{inboxLabel}
					</button>
				)}

				<button
					type='button'
					className={styles.ghostBtn}
					onClick={() => void checkNow()}
					disabled={checking}>
					{checking ? 'Checking…' : "I've verified"}
				</button>

				<button
					type='button'
					className={styles.ghostBtn}
					onClick={() => void sendVerification()}
					disabled={sending || cooldown > 0}>
					{sending
						? 'Sending…'
						: cooldown > 0
							? `Resend in ${cooldown}s`
							: 'Resend'}
				</button>
			</div>

			<button
				type='button'
				className={styles.closeBtn}
				onClick={handleDismiss}
				aria-label='Dismiss for now'>
				×
			</button>
		</div>
	);
};

export default VerifyEmailBanner;
